import { useEffect, useState } from "react"
import { Form, Button } from "react-bootstrap"
import isEmail from "validator/es/lib/isEmail"
import { useDispatch } from "react-redux"
import toast from "react-hot-toast"
import useAuth from "../../../hooks/useAuth"

import { useUpdateEmailMutation } from "../../../app/userApiSlice"
import { useCheckEmailExistsMutation, useReauthenticateUserMutation } from "../../../app/authApiSlice"
import { setCredentials } from "../../../app/authSlice"
import LoadingTextSwap from "../../../components/LoadingTextSwap"
const ChangeEmailForm = () => {
  const dispatch = useDispatch()
  const { email } = useAuth()
  const [updateEmail] = useUpdateEmailMutation()
  const [checkEmailExists] = useCheckEmailExistsMutation()
  const [reauthenticateUser] = useReauthenticateUserMutation()

  const [isLoading, setIsLoading] = useState(false)

  const [newEmail, setNewEmail] = useState(email)
  const [newEmailError, setNewEmailError] = useState("")

  const [password, setPassword] = useState("")
  const [passwordError, setPasswordError] = useState("")

  const [error, setError] = useState("")

  const onEmailChange = (e) => {
    setNewEmail(e.target.value)
    if (newEmailError) {
      if (!newEmail) return setNewEmailError("")

      isEmail(e.target.value) && setNewEmailError("")
    }
  }

  const onPasswordChange = (e) => {
    setPassword(e.target.value)
    passwordError && setPasswordError("")

    if (error === "Incorrect password") {
      setError("")
    }
  }

  useEffect(() => {
    if (!newEmailError && !passwordError) {
      setError("")
    }
  }, [newEmailError, passwordError])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (newEmailError || passwordError) return

    // Validate form inputs
    let error = false

    if (!newEmail) {
      error = true
      setNewEmailError("Email address is required")
    }
    if (newEmail && !isEmail(newEmail)) {
      error = true
      setNewEmailError("Invalid email address")
    }
    if (!password) {
      error = true
      setPasswordError("Password is required")
    }

    if (error) return setError("All fields must be valid")

    // Check if new email already exists
    try {
      setIsLoading(true)

      const { emailExists } = await checkEmailExists(newEmail).unwrap()
      if (emailExists) {
        setIsLoading(false)
        return setNewEmailError("Email already exists")
      }

      // Reauthenticate user
      await reauthenticateUser({ email, password }).unwrap()

      // Update user email
      await updateEmail({ newEmail }).unwrap()

      toast.success("Email updated")
      setPassword("")
      dispatch(setCredentials({ email: newEmail }))

    } catch (error) {
      console.error(error)

      if (error?.status === "functions/already-exists") {
        setError("Email already exists")
      } else if (error?.status === "auth/invalid-credential") {
        setError("Incorrect password")
        setPassword("")
      } else if (error?.data?.message === "Partially updated") {
        toast.warning(
          <div>
            <strong>Email updated with issues</strong>
            <p>
              Your email address was updated, but we encountered an issue while syncing your account data.
              Our team has been notified — no action is needed on your part.
            </p>
          </div>
        )
      } else {
        toast.error("There was an error updating your email address")
      }

    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-100">
      <h3 className="mb-4 fw-bold">Change Email</h3>
      <Form
        className="w-100 mx-auto bg-white shadow-sm rounded-4 p-4"
        onSubmit={handleSubmit}
      >
        <div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">New Email</b>
            <div className="w-100">
              <Form.Group>
                <Form.Control
                  type="email"
                  onChange={onEmailChange}
                  value={newEmail}
                  isInvalid={newEmailError}
                  onBlur={(e) => e.target.value === "" && setNewEmailError("Email address is required")}
                />
                <Form.Control.Feedback type="invalid">
                  <i className="fw-semibold">{newEmailError}</i>
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Confirm Password</b>
            <div className="w-100">
              <Form.Group>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={onPasswordChange}
                  isInvalid={passwordError}
                  onBlur={(e) => e.target.value === "" && setPasswordError("Password is required")}
                />
                <Form.Control.Feedback type="invalid">
                  <i className="fw-semibold">{passwordError}</i>
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
        </div>
        {error && <p className="text-danger text-center fw-semibold i mt-2">{error}</p>}
        <Button
          disabled={isLoading}
          className="px-5 mt-3 rounded-3 mx-auto d-block"
          variant="dark"
          type="submit"
        >
          <LoadingTextSwap isLoading={isLoading}>
            Save
          </LoadingTextSwap>
        </Button>
      </Form>
    </div>
  )
}
export default ChangeEmailForm